import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Desktop, Mobile } from '../../App';
import { useAppSelector } from '../../app/hooks';
import { AnimatedDashboard } from '../../components/animation/AnimatedDashboard';
import Spinner from '../../components/common/Spinner';
import { SectionSeparatorVertical } from '../../styles/GlobalComponents';
import { EarningsInfo } from './Earnings/Earnings';
import { EarningsPageWrap } from './EarningsBackofficeStyled';
import { EarningsHistory } from './EarningsHistory/EarningsHistory';
import { getEarningHistory, initEarnings } from './Earnings.actions';
import {
  EarningsPageEnum,
  EarningsHistoryFilterEnum,
  EarningsSortByEnum,
  FilterToggleEnum
} from './Earnings.types';
import { UserTypes } from '../../types/loginTypes';

export const EarningsBackoffice = () => {
  const [withdraw, setWithdraw] = useState(false);
  const [topUp, setTopUp] = useState(false);
  const [transfer, setTransfer] = useState(false);
  const [mobilePage, setMobilePage] = useState(EarningsPageEnum.EARNINGS_INFO);
  const [numberOfPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(50);
  const [sort, setSort] = useState<EarningsSortByEnum | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchId, setSearchId] = useState<number | string>('');
  const [onMount, setOnMount] = useState(false);

  const { loading, info } = useAppSelector((state) => state.earnings);

  const dispatch = useDispatch();

  const { userInfo } = useAppSelector((state) => state.auth);

  const isNotRestrictedUser = userInfo.userType === UserTypes.Regular;

  useEffect(() => {
    if (isNotRestrictedUser)
      dispatch(initEarnings({ numberOfPage, countPerPage }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const [filterToggle, setFilterToggle] = useState<FilterToggleEnum | null>(
    null
  );
  const [earningsType, setEarningsType] = useState<number[] | null>(null);
  const [filter, setFilter] = useState<EarningsHistoryFilterEnum | null>(null);

  const HandleChangeFilter = (filter: EarningsHistoryFilterEnum | null) => {
    setFilter(filter);
    setFilterToggle(null);

    switch (filter) {
      case EarningsHistoryFilterEnum.TEAM_BONUS:
        return setEarningsType([1]);
      case EarningsHistoryFilterEnum.DIRECT_BONUS:
        return setEarningsType([2]);
      case EarningsHistoryFilterEnum.MATCHING_BONUS:
        return setEarningsType([3]);
      case EarningsHistoryFilterEnum.CUSTOMER_BONUS:
        return setEarningsType([4]);
      case EarningsHistoryFilterEnum.CASHBACK_BONUS:
        return setEarningsType([5]);
      case EarningsHistoryFilterEnum.GLOBAL_POOL_BONUS:
        return setEarningsType([6]);
      case EarningsHistoryFilterEnum.TOP_PERFORMER_BONUS:
        return setEarningsType([7]);
      case EarningsHistoryFilterEnum.PACKAGE_RANK:
        return setEarningsType([8]);
      case EarningsHistoryFilterEnum.HODL_POOL_BONUS:
        return setEarningsType([9]);
      case EarningsHistoryFilterEnum.TRADING_BONUS:
        return setEarningsType([11]);
      case EarningsHistoryFilterEnum.OTHER_BONUSES:
        return setEarningsType([5, 6, 7, 9, 10]);
      case EarningsHistoryFilterEnum.LOYALITY:
        return setEarningsType([12]);
      case EarningsHistoryFilterEnum.MYLIFE:
        return setEarningsType([13]);
      case null:
        return setEarningsType(null);
      default:
        return;
    }
  };

  const getSortByAndSortOrferFromSort = useCallback(() => {
    switch (sort) {
      case 'dateUp':
        return ['dateCreated', 'DESC'];
      case 'dateDown':
        return ['dateCreated', 'ASC'];
      case 'priceUp':
        return ['amount', 'ASC'];
      case 'priceDown':
        return ['amount', 'DESC'];

      default:
        return ['dateCreated', 'DESC'];
    }
  }, [sort]);

  const getStatusFromFilter = useCallback(() => {
    switch (filter) {
      case EarningsHistoryFilterEnum.PAID:
        return 2;
      case EarningsHistoryFilterEnum.PENDING:
        return 1;
      default:
        return '';
    }
  }, [filter]);

  useEffect(() => {
    if (onMount) {
      const [sortBy, sortOrder] = getSortByAndSortOrferFromSort();
      let dFrom = startDate
        ? `${moment(startDate).format('YYYY-MM-DD')}T00:00:00.000Z`
        : '';
      let dTo = endDate
        ? `${moment(endDate).format('YYYY-MM-DD')}T23:59:59.999Z`
        : '';
      if (dFrom === '' || dTo === '') {
        dFrom = '';
        dTo = '';
      }
      dispatch(
        getEarningHistory({
          id: searchId,
          sortBy,
          sortOrder,
          fromDate: dFrom,
          toDate: dTo,
          status: getStatusFromFilter(),
          earningsTypes: earningsType?.[0],
          numberOfPage,
          countPerPage
        })
      );
    } else {
      setOnMount(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter,
    sort,
    startDate,
    endDate,
    searchId,
    earningsType,
    numberOfPage,
    countPerPage
  ]);

  if (loading && !info) return <Spinner fullScreen={true} />;

  return (
    <AnimatedDashboard zoom>
      <Desktop style={{ width: '100%' }}>
        <EarningsPageWrap>
          <EarningsInfo
            setMobilePage={setMobilePage}
            withdraw={withdraw}
            setWithdraw={setWithdraw}
            setTopUp={setTopUp}
            topUp={topUp}
            transfer={transfer}
            setTransfer={setTransfer}
            HandleChangeFilter={HandleChangeFilter}
          />
          <SectionSeparatorVertical />
          <EarningsHistory
            setCountPerPage={setCountPerPage}
            countPerPage={countPerPage}
            filterToggle={filterToggle}
            setFilterToggle={setFilterToggle}
            setEarningsType={setEarningsType}
            filter={filter}
            setFilter={setFilter}
            HandleChangeFilter={HandleChangeFilter}
            sort={sort}
            setSort={setSort}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setSearchId={setSearchId}
            setMobilePage={setMobilePage}
          />
        </EarningsPageWrap>
      </Desktop>
      <Mobile>
        {mobilePage === EarningsPageEnum.EARNINGS_INFO && (
          <EarningsInfo
            setMobilePage={setMobilePage}
            setWithdraw={setWithdraw}
            withdraw={withdraw}
            setTopUp={setTopUp}
            topUp={topUp}
            transfer={transfer}
            setTransfer={setTransfer}
            HandleChangeFilter={HandleChangeFilter}
          />
        )}
        {mobilePage === EarningsPageEnum.EARNINGS_HISTORY && (
          <EarningsHistory
            setCountPerPage={setCountPerPage}
            countPerPage={countPerPage}
            filterToggle={filterToggle}
            setFilterToggle={setFilterToggle}
            setEarningsType={setEarningsType}
            filter={filter}
            setFilter={setFilter}
            HandleChangeFilter={HandleChangeFilter}
            sort={sort}
            setSort={setSort}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setSearchId={setSearchId}
            setMobilePage={setMobilePage}
          />
        )}
      </Mobile>
    </AnimatedDashboard>
  );
};
